import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Center, Spinner } from '@chakra-ui/react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminLayout from './layouts/AdminLayout';
import ParentLayout from './layouts/ParentLayout';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';

import StudentsPage from './pages/StudentsPage';
import OldStudentsPage from './pages/OldStudentsPage';
import StaffPage from './pages/StaffPage';
import RollCallPage from './pages/RollCallPage';
import DailyAttendancePage from './pages/DailyAttendancePage';
import FrontdeskPage from './pages/FrontdeskPage';
import MyClassPage from './pages/MyClassPage';
import PickupMonitorPage from './pages/PickupMonitorPage';
import ParentPickupPage from './pages/ParentPickupPage';
import ParentDashboard from './pages/ParentDashboard';

import InstitutionsPage from './pages/InstitutionsPage';
import ClassesPage from './pages/ClassesPage';
import LeavesPage from './pages/LeavesPage'; // Phase 23
import EnglishMakeupPage from './pages/EnglishMakeupPage'; // Phase 27
import SettingsPage from './pages/SettingsPage';
import ActivationPage from './pages/ActivationPage';
import TeacherAttendancePage from './pages/TeacherAttendancePage';
import CommunicationBookPage from './pages/CommunicationBookPage'; // Phase 28
import TeacherCommBookHistoryPage from './pages/TeacherCommBookHistoryPage';
import TeacherChatPage from './pages/TeacherChatPage'; // 桌面版親師對話（取代側欄「聯絡簿紀錄」入口）
import TeacherBroadcastPage from './pages/TeacherBroadcastPage'; // 桌面版整班群發
import ParentCommBookPage from './pages/ParentCommBookPage'; // Phase 28
import ParentChatListPage from './pages/ParentChatListPage'; // [B3] 親師對話（家長）
import ParentChatRoomPage from './pages/ParentChatRoomPage'; // [B3] 親師對話（家長）
import ParentLeavePage from './pages/ParentLeavePage'; // Phase 30
import ParentLunchPage from './pages/ParentLunchPage'; // Phase 29
import ParentAnnouncementsPage from './pages/ParentAnnouncementsPage'; // Phase 30 (parent web)
import ParentAnnouncementDetailPage from './pages/ParentAnnouncementDetailPage'; // Phase 30 (parent web)
import LunchManagementPage from './pages/LunchManagementPage'; // Phase 29
import ReportsPage from './pages/ReportsPage'; // Phase 31
import AnnouncementsAdminPage from './pages/AnnouncementsAdminPage'; // Phase 30
import AnnouncementEditPage from './pages/AnnouncementEditPage'; // Phase 30
import ReloadPrompt from './components/ReloadPrompt';
import InstitutionStatusPage from './pages/InstitutionStatusPage'; // Phase 29

import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import LoadingScreen from './components/LoadingScreen';

// 根路徑重定向組件
// 老師手機 PWA 的網域。親師對話的路由只存在於該站，桌面控制台沒有對應頁面。
// 用 env 覆寫的空間留著（不同機構/測試站可能不同），未設定時用正式站。
const TEACHER_PWA_ORIGIN = import.meta.env.VITE_TEACHER_PWA_ORIGIN || 'https://mpwa.naturaledu.net';

// 把 /chat/* 轉送到老師 PWA 的同一路徑（見下方路由處的說明）。
// 用 window.location.replace 而非 assign：這是一次「修正落點」的轉送，
// 不該在瀏覽器歷史留下一筆桌面站的 /chat（否則使用者按上一頁會回到這個不存在的頁面再被轉走）。
const RedirectToTeacherPwa = () => {
    const { pathname, search } = useLocation();
    useEffect(() => {
        window.location.replace(`${TEACHER_PWA_ORIGIN}${pathname}${search}`);
    }, [pathname, search]);
    return null;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 根據角色導向不同頁面
  if (user.role === 'parent') {
    return <Navigate to="/parent/dashboard" replace />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

import usePushNotifications from './hooks/usePushNotifications';

function App() {
  // Version Check Log
  console.log("🚀 App Version: 2025-01-01-V4-MobileScrollFix");

  // Initialize Push Notifications
  usePushNotifications();

  return (
    <>
      <ReloadPrompt />
      <Routes>
        {/* 公開路由 (靜態頁面) */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* 公開路由 (登入頁) */}
        <Route path="/activated" element={<Navigate to="/login" />} />
        <Route path="/activate" element={<ActivationPage />} />

        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />

        {/* 管理員/老師 路由 */}
        <Route element={
          <PrivateRoute allowedRoles={['super_admin', 'admin', 'receptionist', 'teacher']}>
            <AdminLayout />
          </PrivateRoute>
        }>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/institution-status" element={<InstitutionStatusPage />} /> {/* Phase 29 */}
          <Route path="/students" element={<StudentsPage />} />
          <Route path="/archived-students" element={<OldStudentsPage />} />
          {/* 拆分人員管理 */}
          {/* 人員管理僅 admin/super_admin。Sidebar 入口本來就只給這兩者（見 Sidebar.jsx
              「人員管理」「教職員管理」兩個 role 分支），但路由層原本沒有守衛——任何教職員
              直接打 /staff 都拿得到完整頁面，含新增按鈕與帶「角色」下拉的編輯 Modal。
              後端 PUT/DELETE /users/{id} 已於 2026-08-21 一併收緊，這裡補上對應的路由防護。 */}
          <Route path="/staff" element={
            <PrivateRoute allowedRoles={['super_admin', 'admin']}>
              <StaffPage viewMode="staff" />
            </PrivateRoute>
          } />
          <Route path="/parents" element={
            <PrivateRoute allowedRoles={['super_admin', 'admin']}>
              <StaffPage viewMode="parent" />
            </PrivateRoute>
          } />
          <Route path="/all-users" element={
            <PrivateRoute allowedRoles={['super_admin', 'admin']}>
              <StaffPage viewMode="all" />
            </PrivateRoute>
          } />

          <Route path="/roll-call" element={<RollCallPage />} />
          <Route path="/frontdesk" element={<FrontdeskPage />} />
          <Route path="/daily-attendance" element={<DailyAttendancePage />} />
          <Route path="/my-class" element={<MyClassPage />} />
          <Route path="/pickup-monitor" element={<PickupMonitorPage />} />
          <Route path="/teacher/comm-book" element={<CommunicationBookPage />} /> {/* Phase 28 */}
          {/* 側欄入口已改指向 /teacher/chat；這條路由刻意保留，舊書籤與外部連結仍然打得開 */}
          <Route path="/teacher/history-comm-book" element={<TeacherCommBookHistoryPage />} />
          <Route path="/teacher/chat" element={<TeacherChatPage />} />
          <Route path="/teacher/broadcast" element={<TeacherBroadcastPage />} />

          <Route path="/classes" element={<ClassesPage />} />
          <Route path="/leaves" element={<LeavesPage />} /> {/* Phase 23 */}
          <Route path="/lunch" element={<LunchManagementPage />} /> {/* Phase 29 */}
          <Route path="/reports" element={<ReportsPage />} /> {/* Phase 31 */}
          <Route path="/announcements" element={<AnnouncementsAdminPage />} /> {/* Phase 30 */}
          <Route path="/announcements/new" element={<AnnouncementEditPage />} />
          <Route path="/announcements/:id/edit" element={<AnnouncementEditPage />} />
          <Route path="/makeup" element={<EnglishMakeupPage />} /> {/* Phase 27 */}
          {/* [A2] 機構管理僅 super_admin 可用；一般 admin/receptionist/teacher 直接打 URL 會被導回首頁。
              Sidebar 入口本來就只有 super_admin 看得到（見 components/Sidebar.jsx），這裡補上路由層防護。 */}
          <Route path="/institutions" element={
            <PrivateRoute allowedRoles={['super_admin']}>
              <InstitutionsPage />
            </PrivateRoute>
          } />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/teacher/attendance" element={<TeacherAttendancePage />} />
          {/* 其他管理頁面將在這裡新增 */}
        </Route>


        {/* 家長 路由 */}
        <Route element={
          <PrivateRoute allowedRoles={['parent']}>
            <ParentLayout />
          </PrivateRoute>
        }>
          <Route path="/parent/dashboard" element={<ParentDashboard />} />
          <Route path="/pickup" element={<ParentPickupPage />} />
          <Route path="/parent/communication-book" element={<ParentCommBookPage />} />
          {/* [2026-09-07 修復] 家長 web 版缺這兩條路由，導致「聯絡簿頁面跳不進去」。
              MobileBottomNav.jsx 的「聯絡簿」入口在 chatReady（全域 chat_enabled ＋ 該生機構
              features.chat，天籟兩者皆成立）時指向 /parent/chat——但這兩條路由過去只加在
              AppParent.jsx（家長**原生 App**）裡，本檔（家長 **web/PWA**）從來沒有。
              於是 web 版家長一點「聯絡簿」就落到本檔最後的 path="*"，被當成 404 導回首頁，
              看起來就是「怎麼點都進不去」。與檔案下方 /chat/* 那條註解記錄的是同一類錯誤
              （導航指向本檔沒有的路由 → 被 catch-all 吃掉）。
              原生 App 家長不受影響（AppParent.jsx 有這兩條），所以只有少數用網頁版的家長中招。
              路徑逐字對齊 B2 推播 deep-link 約定 /parent/chat/{conversation_id}；旗標 off 時
              兩個頁面內部都會自行 Navigate 回上面的舊版聯絡簿頁，不需要再包一層守衛。 */}
          <Route path="/parent/chat" element={<ParentChatListPage />} />
          <Route path="/parent/chat/:id" element={<ParentChatRoomPage />} />
          <Route path="/parent/leaves" element={<ParentLeavePage />} /> {/* Phase 30 */}
          <Route path="/parent/lunch" element={<ParentLunchPage />} /> {/* Phase 29 */}
          <Route path="/parent/announcements" element={<ParentAnnouncementsPage />} /> {/* Phase 30 */}
          <Route path="/parent/announcements/:id" element={<ParentAnnouncementDetailPage />} />
          <Route path="/parent/settings" element={<SettingsPage />} />
        </Route>

        {/* 根路徑處理 */}
        <Route path="/" element={<RootRedirect />} />

        {/* [2026-08-12] 親師對話的深連結（/chat/:id）是老師手機 PWA 的路由，桌面控制台沒有。
            但推播通知帶的 link 是相對路徑，若老師的推播訂閱是在這個網域（naturaledu.net）授權的，
            點通知就會落在這裡，然後被下方的 path="*" 當成 404 導回首頁——正是「點訊息不會跳到
            對話框」的成因之一。這裡改為轉送到 PWA 網域的同一路徑，讓通知不論落在哪個網域
            都能到達正確的對話。 */}
        <Route path="/chat/*" element={<RedirectToTeacherPwa />} />

        {/* 404 Not Found - 導回首頁 (讓 RootRedirect 決定去哪) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
